import { useEffect, useMemo, useState } from "react";
import { listContacts } from "../../../../../api/contacts";
import { listLocations } from "../../../../../api/locationOfGoods";
import NewSectionModal from "./components/NewSectionModal";
import { listCommodityCodes } from "../../../../../api/commodity-codes";
import SearchPageDrawer from "./components/Drawer/SearchPageDrawer";
import SearchPageSelectedItem from "./components/SelectedItem/SearchPageSelectedItem";
import ErrorModal from "../../../../modals/ErrorModal";
import EmptySearchPageMessage from "./components/EmptySearchPageMessage";

/**
 * SearchPage component is used in both of the following scenarios:
 *   - In the Contacts, Goods Locations and Commodity Codes pages for creating, updating and deleting entries
 *   - In the corresponding declaration sections for each in order to search and select a saved entry
 *
 * The "standalone" prop is applied when the component is used in the first scenario
 *  */

// TODO: Move search and filtered items to SearchPageDrawer
const SearchPage = (props) => {
	const [items, setItems] = useState([]);
	const [isDrawerExpanded, setIsDrawerExpanded] = useState(true);
	const [searchValue, setSearchValue] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [selectedItem, setSelectedItem] = useState();
	const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
	const [isNewItemModalOpen, setIsNewItemModalOpen] = useState(false);
	const {
		searchResultElements,
		initialSearchValue,
		onSelectedItemChange,
		searchType,
		standalone,
		style,
		isInModal,
	} = props;

	const getItemData = (item) => {
		let data = {};

		if (searchType === "contacts") {
			data = item.jdeclaration.data.contact;
		} else if (searchType === "locationOfGoods") {
			data = item.goodsLocation.data;
		} else if (searchType === "commodityCodes") {
			data = item.commodityCodes.data;
		}

		return data;
	};

	const filteredItems = useMemo(() => {
		let filteredItems = [];
		if (!searchValue) {
			return items;
		}
		const lowerCaseSearchValue = String(searchValue).toLowerCase();

		items?.forEach((item) => {
			let itemMatches = false;
			const data = getItemData(item);

			if (data) {
				Object.keys(data).forEach((key) => {
					if (String(data[key]).toLowerCase().includes(lowerCaseSearchValue)) {
						itemMatches = true;
					}
				});

				if (itemMatches) {
					filteredItems.push(item);
				}
			}
		});

		return filteredItems;
	}, [items]);

	const fetchData = async () => {
		setIsLoading(true);
		try {
			if (searchType === "contacts") {
				const result = await listContacts();
				setItems(result.items);
			} else if (searchType === "locationOfGoods") {
				const result = await listLocations();
				setItems(result.items);
			} else if (searchType === "commodityCodes") {
				const result = await listCommodityCodes();
				setItems(result.items);
			}
		} catch (e) {
			console.log(e);
			setIsErrorModalOpen(true);
		}
		setIsLoading(false);
	};

	const handleDelete = async () => {
		const deletedItemIndex = items.findIndex((item) => item.creationTime === selectedItem.creationTime);

		if (deletedItemIndex > -1) {
			items.splice(deletedItemIndex, 1);

			setItems([...items]);
		}

		setSelectedItem(undefined);
	};

	useEffect(() => {
		setSearchValue(initialSearchValue);
	}, [initialSearchValue]);

	useEffect(() => {
		fetchData();
	}, []);

	useEffect(() => {
		onSelectedItemChange?.(selectedItem ? getItemData(selectedItem) : null);
	}, [selectedItem]);

	return (
		<div style={{ width: "100%", height: "100%", ...style }}>
			<ErrorModal isOpen={isErrorModalOpen} onClose={() => setIsErrorModalOpen(false)} />

			<NewSectionModal
				isOpen={isNewItemModalOpen}
				onClose={() => setIsNewItemModalOpen(false)}
				sectionType={searchType}
				sectionElements={searchResultElements}
				onSubmit={fetchData}
			/>

			<div style={{ display: "flex", width: "100%", height: "100%" }}>
				<SearchPageDrawer
					isInModal={isInModal}
					searchType={searchType}
					setIsNewItemModalOpen={setIsNewItemModalOpen}
					onSearchInputChange={(e) => {
						setSearchValue(e.target.value);
					}}
					searchValue={searchValue}
					isLoading={isLoading}
					filteredItems={filteredItems}
					isExpanded={isDrawerExpanded}
					selectedItem={selectedItem}
					setSelectedItem={setSelectedItem}
					setIsExpanded={setIsDrawerExpanded}
				/>

				<div
					style={{
						transition: "0.3s",
						width: `calc(100% - ${isDrawerExpanded ? 320 : 64}px)`,
						paddingTop: isInModal ? 40 : 0,
						paddingBottom: isInModal ? 40 : 0,
						paddingLeft: 40,
						paddingRight: 40,
						borderTopRightRadius: 6,
						borderBottomRightRadius: 6,
						backgroundColor: isInModal ? "#F7FCF9" : "",
						overflowY: "auto",
					}}
				>
					{!items.length && !isLoading ? (
						<EmptySearchPageMessage
							searchType={searchType}
							onActionButtonClick={() => setIsNewItemModalOpen(true)}
						/>
					) : (
						<SearchPageSelectedItem
							isInModal={isInModal}
							searchType={searchType}
							item={selectedItem}
							elements={searchResultElements}
							standalone={standalone}
							onBackClick={() => setSelectedItem(null)}
							onDelete={handleDelete}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default SearchPage;
